import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, ShoppingCart, ArrowRight, Loader2, ArrowLeft } from "lucide-react";

const Cart = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [removing, setRemoving] = useState(null);

  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cart/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error("Failed to fetch cart", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleRemove = async (itemId, title) => {
    setRemoving(itemId);
    try {
      await axios.delete(`${API}/cart/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${title} removed from cart`);
      fetchCart();
    } catch (error) {
      toast.error("Failed to remove item");
    } finally {
      setRemoving(null);
    }
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <Link 
            to="/library" 
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-gold mb-6 group"
          >
            <ArrowLeft size={16} className="transform group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="text-sm uppercase tracking-widest">Continue Shopping</span>
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl text-[#EAEAEA] flex items-center gap-4">
            <ShoppingCart size={40} className="text-gold" />
            Your Cart
          </h1>
        </div>

        {cart.items.length === 0 ? (
          <div className="text-center py-20 border border-white/5 rounded-sm" data-testid="empty-cart">
            <ShoppingCart size={48} className="mx-auto text-[#A1A1AA] mb-4" />
            <h2 className="font-heading text-2xl text-[#EAEAEA] mb-2">Your cart is empty</h2>
            <p className="text-[#A1A1AA] mb-6">Explore the library to find your path.</p>
            <Link to="/library">
              <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none uppercase text-xs tracking-widest">
                Browse Library
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="space-y-4 mb-8" data-testid="cart-items">
              {cart.items.map((item) => (
                <div 
                  key={item.id} 
                  className="flex gap-6 p-6 border border-white/5 rounded-sm bg-paper"
                  data-testid={`cart-item-${item.book_id}`}
                >
                  {/* Image */}
                  <div className="w-20 h-28 flex-shrink-0 overflow-hidden rounded-sm">
                    <img
                      src={item.cover_image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1">
                    <Link 
                      to={`/book/${item.book_id}`} 
                      className="font-heading text-xl text-[#EAEAEA] hover:text-gold transition-colors duration-300"
                    >
                      {item.title}
                    </Link>
                    {item.subtitle && (
                      <p className="text-[#A1A1AA] text-sm italic">{item.subtitle}</p>
                    )}
                  </div>

                  {/* Price & Remove */}
                  <div className="flex flex-col items-end justify-between">
                    <span className="font-mono text-xl text-gold">${item.price.toFixed(2)}</span>
                    <button
                      onClick={() => handleRemove(item.id, item.title)}
                      disabled={removing === item.id}
                      className="text-[#A1A1AA] hover:text-red-400 transition-colors duration-300 p-2"
                      data-testid={`remove-${item.book_id}`}
                    >
                      {removing === item.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="border border-gold/20 rounded-sm bg-gold/5 p-8" data-testid="cart-summary">
              <div className="flex items-center justify-between mb-6">
                <span className="text-[#A1A1AA]">Subtotal ({cart.item_count} items)</span>
                <span className="font-mono text-2xl text-gold" data-testid="cart-total">
                  ${cart.total.toFixed(2)} AUD
                </span>
              </div>
              <Button
                onClick={handleCheckout}
                disabled={checkingOut}
                className="w-full bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 uppercase text-xs tracking-widest btn-shine"
                data-testid="checkout-btn"
              >
                {checkingOut ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    Proceed to Checkout
                    <ArrowRight size={16} className="ml-2" />
                  </>
                )}
              </Button>
              <p className="text-[#A1A1AA] text-xs text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
